import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { guardedFetch } from '../lib/liveWrites.js';

/* The conversational intake for a Claude account request.
 *
 * THIS COMPONENT KNOWS NOTHING ABOUT SCORING, AND THAT IS THE DESIGN.
 * The API hands it three fields - question, choices, done - and there is
 * nothing else in the response to hold. Do not add client state that mirrors a
 * recommendation, and do not "just fetch" the assessment to show a preview:
 * there is no endpoint that would give it to a requester, on purpose.
 *
 * One question at a time. The server keeps the transcript, so a refresh mid-way
 * loses only the unsent answer, and every question already asked is recorded
 * even if the person walks away - which is a signal we want.
 *
 * ---------------------------------------------------------------------------
 * WHY THERE IS SO MUCH WAITING MACHINERY IN HERE.
 *
 * Ryan used this live on 2026-09-08 and clicked his answer several times,
 * believing it had not registered. Nothing was broken - the buttons were
 * already `disabled={busy}` and a GROUP BY on (INTAKE_ID, TURN_NUMBER) proves
 * no duplicate turn was ever created. The problem was that the page showed no
 * change whatsoever between the click and the next question.
 *
 * Do not try to fix this by speeding up the model. That was measured: the call
 * is a median 4.6s, and turning off adaptive thinking saves 2.6s across a whole
 * conversation while making the questions worse. Five seconds a turn is the
 * floor for this model, so the job is to make five seconds legible:
 *
 *   1. the answer you picked appears instantly, before the server replies
 *   2. an animated "working on it" line, never a silent gap
 *   3. the transcript stays on screen, so progress is visible
 *   4. disabled controls that actually look disabled
 * ---------------------------------------------------------------------------
 */

export default function ClaudeSeatChat({ onBack }) {
  const [intakeId, setIntakeId] = useState(null);
  const [turn, setTurn] = useState(null);
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Questions already answered, kept client-side purely so the page has
  // something to show. The server transcript remains the record of truth.
  const [log, setLog] = useState([]);
  // The answer in flight. Echoing it back is what makes the click feel handled.
  const [pending, setPending] = useState(null);
  const navigate = useNavigate();
  // React 18+ mounts twice in StrictMode. Without this the conversation starts
  // twice and the person sees the second one, orphaning the first transcript.
  const started = useRef(false);

  async function step(nextAnswer) {
    setBusy(true);
    setError('');
    // Show it immediately. If the request fails this is rolled back below, so
    // the page never claims an answer the server did not take.
    if (nextAnswer) setPending(nextAnswer);
    const askedQuestion = turn?.question;
    try {
      const res = await guardedFetch('/api/intake/turn', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ intakeId, answer: nextAnswer }),
      });
      if (res.status === 410) throw new Error('This chat has been retired. Please use the request form instead.');
      if (!res.ok) throw new Error('That did not go through. Try again.');
      const data = await res.json();
      if (nextAnswer && askedQuestion) {
        setLog((prev) => [...prev, { q: askedQuestion, a: nextAnswer }]);
      }
      setIntakeId(data.intakeId);
      setTurn(data);
      setAnswer('');
    } catch (e) {
      setError(e.message);
    } finally {
      // Cleared on both paths: on success the answer has moved into the log, on
      // failure it must not linger as though it had been accepted.
      setPending(null);
      setBusy(false);
    }
  }

  async function submit() {
    setBusy(true);
    setError('');
    try {
      const res = await guardedFetch('/api/intake/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ intakeId }),
      });
      if (res.status === 410) throw new Error('This chat has been retired. Please use the request form instead.');
      if (!res.ok) throw new Error('Your answers are saved, but filing the request failed. Try again.');
      const data = await res.json();
      navigate(`/ticket/${data.ticketNo}`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    step('');
    // Runs once, on mount, to fetch the opening question.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* The very first question. This is the slowest moment in the whole flow - a
   * cold function pays roughly 3.8s for Key Vault before the model call even
   * starts - so it says so rather than showing a bare word and hoping. */
  if (!turn && !error) {
    return (
      <div className="card card--pad" style={{ marginTop: 20 }}>
        <div className="seat-thinking">
          <span className="seat-thinking__dots" aria-hidden="true"><i /><i /><i /></span>
          <span>Starting the conversation. The first question takes a few seconds…</span>
        </div>
      </div>
    );
  }

  const canType = !turn?.done && !turn?.choices?.length;

  return (
    <div className="card card--pad" style={{ marginTop: 20 }}>
      {/* aria-live so a screen reader announces each new question rather than
          the page changing silently. */}
      <div aria-live="polite" aria-atomic="false">
        {log.length ? (
          <ul className="seat-log">
            {log.map((entry, i) => (
              <li key={`${entry.q}-${i}`}>
                <p className="seat-log__q">{entry.q}</p>
                <p className="seat-log__a">{entry.a}</p>
              </li>
            ))}
          </ul>
        ) : null}

        <p style={{ fontSize: 'var(--ihs-fs-h3)', marginTop: 0, marginBottom: 14 }}>
          {turn?.question}
        </p>
      </div>

      {error ? (
        <div className="banner" style={{ marginBottom: 14 }}>
          <span className="material-symbols-outlined" aria-hidden="true">error</span>
          <span>{error}</span>
        </div>
      ) : null}

      {turn?.done ? (
        <div className="inline" style={{ marginTop: 8 }}>
          <button type="button" className="btn btn--primary" onClick={submit} disabled={busy}>
            {busy ? 'Sending…' : 'Send my request to IT'}
          </button>
        </div>
      ) : null}

      {!turn?.done && turn?.choices?.length ? (
        <div className="opts">
          {turn.choices.map((c) => (
            <button
              key={c}
              type="button"
              className={`o${pending === c ? ' o--pending' : ''}`}
              disabled={busy}
              onClick={() => step(c)}
            >
              {c}
            </button>
          ))}
        </div>
      ) : null}

      {canType ? (
        <form onSubmit={(e) => { e.preventDefault(); step(answer); }}>
          <div className="field">
            <textarea
              className="textarea"
              rows={3}
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="In your own words"
              disabled={busy}
            />
          </div>
          <button type="submit" className="btn btn--primary" disabled={busy || !answer.trim()}>
            {busy ? 'Thinking…' : 'Next'}
          </button>
        </form>
      ) : null}

      {/* The working line sits BELOW the controls, where the eye already is
          after a click, and replaces the silent gap that caused the repeat
          clicking. It appears for a typed answer too, not just a chosen one. */}
      {busy && !turn?.done ? (
        <div className="seat-thinking" style={{ marginTop: 12 }} aria-live="polite">
          <span className="seat-thinking__dots" aria-hidden="true"><i /><i /><i /></span>
          <span>{pending ? 'Got it - working out what to ask next…' : 'Working on it…'}</span>
        </div>
      ) : null}

      {onBack ? (
        <div className="inline" style={{ marginTop: 16 }}>
          <button type="button" className="btn btn--ghost" onClick={onBack} disabled={busy}>
            Back
          </button>
        </div>
      ) : null}
    </div>
  );
}
